import {
  createAudioAnalyser,
  type DisconnectReason,
  type Participant,
  ParticipantKind,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrack,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import {
  isVoiceAgentState,
  LK_AGENT_IDENTITY_PREFIX,
  LK_AGENT_STATE_ATTR,
  LK_TRANSCRIPTION_TOPIC,
  VoiceAgentState,
  VoiceCaption,
  VoiceSession,
} from '../types/voice-call.types';

/**
 * Owns the single LiveKit `Room` for the app. Kept out of React so the call survives
 * the `/chat` → `/chat/:conversationId` remount; UI state lives in `useVoiceCallStore`.
 */

export interface VoiceRoomCallbacks {
  onAgentJoined: () => void;
  onAgentLeft: () => void;
  onAgentState: (state: VoiceAgentState) => void;
  onCaption: (caption: VoiceCaption) => void;
  onDisconnected: (reason?: DisconnectReason) => void;
  onMediaDeviceError: (error: Error) => void;
}

type AgentAnalyser = ReturnType<typeof createAudioAnalyser>;

const AUDIO_HOST_ID = 'voice-call-audio';

let room: Room | null = null;
let analyser: AgentAnalyser | null = null;
let audioHost: HTMLElement | null = null;

const isAgent = (participant?: Participant | null): boolean =>
  !!participant &&
  (participant.kind === ParticipantKind.AGENT ||
    participant.identity.startsWith(LK_AGENT_IDENTITY_PREFIX));

const ensureAudioHost = (): HTMLElement => {
  if (!audioHost) {
    audioHost = document.createElement('div');
    audioHost.id = AUDIO_HOST_ID;
    audioHost.style.display = 'none';
    document.body.appendChild(audioHost);
  }
  return audioHost;
};

const dropAnalyser = () => {
  const current = analyser;
  analyser = null;
  current?.cleanup().catch(() => undefined);
};

const cleanupLocals = () => {
  dropAnalyser();
  audioHost?.remove();
  audioHost = null;
  room = null;
};

const emitAgentState = (participant: Participant, cb: VoiceRoomCallbacks) => {
  const state = participant.attributes[LK_AGENT_STATE_ATTR];
  if (isVoiceAgentState(state)) cb.onAgentState(state);
};

/**
 * Ask for the microphone before creating a session so a denied mic never leaves an
 * orphan conversation behind. The stream is released immediately; LiveKit re-acquires it.
 */
export const requestMicPermission = async (): Promise<void> => {
  if (typeof window === 'undefined' || !navigator?.mediaDevices?.getUserMedia) {
    throw new Error('Browser does not support microphone access');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => track.stop());
};

export const connectVoiceRoom = async (
  session: VoiceSession,
  cb: VoiceRoomCallbacks
): Promise<void> => {
  await disconnectVoiceRoom();

  const nextRoom = new Room({
    // Browser AEC is what lets the user talk over the agent without it hearing itself.
    audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  room = nextRoom;

  // Must be registered before connect or early segments are dropped. Agent transcripts are
  // delta streams synced to playback, so the caption is updated chunk by chunk rather than
  // waiting for the stream to close.
  nextRoom.registerTextStreamHandler(LK_TRANSCRIPTION_TOPIC, async (reader, participantInfo) => {
    const attrs = reader.info.attributes ?? {};
    const segmentId = attrs['lk.segment_id'] ?? reader.info.id;
    const who = participantInfo.identity.startsWith(LK_AGENT_IDENTITY_PREFIX) ? 'agent' : 'user';
    let text = '';
    for await (const chunk of reader) {
      text += chunk;
      cb.onCaption({ segmentId, who, text, isFinal: false });
    }
    cb.onCaption({ segmentId, who, text, isFinal: attrs['lk.transcription_final'] === 'true' });
  });

  nextRoom.on(
    RoomEvent.TrackSubscribed,
    (track: RemoteTrack, _publication, participant: RemoteParticipant) => {
      if (track.kind !== Track.Kind.Audio) return;
      const element = track.attach();
      element.id = `lk-${track.sid}`;
      ensureAudioHost().appendChild(element);
      if (isAgent(participant)) {
        dropAnalyser();
        analyser = createAudioAnalyser(track as RemoteAudioTrack, {
          fftSize: 256,
          smoothingTimeConstant: 0.6,
        });
      }
    }
  );
  nextRoom.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    track.detach().forEach((element) => element.remove());
    if (track.kind === Track.Kind.Audio) dropAnalyser();
  });
  nextRoom.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
    if (!isAgent(participant)) return;
    cb.onAgentJoined();
    emitAgentState(participant, cb);
  });
  nextRoom.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
    if (isAgent(participant)) cb.onAgentLeft();
  });
  nextRoom.on(
    RoomEvent.ParticipantAttributesChanged,
    (changed: Record<string, string>, participant: Participant) => {
      if (!isAgent(participant)) return;
      const state = changed[LK_AGENT_STATE_ATTR];
      if (isVoiceAgentState(state)) cb.onAgentState(state);
    }
  );
  nextRoom.on(RoomEvent.MediaDevicesError, (error: Error) => cb.onMediaDeviceError(error));
  nextRoom.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    if (room === nextRoom) cleanupLocals();
    cb.onDisconnected(reason);
  });

  await nextRoom.connect(session.url, session.token);
  await nextRoom.localParticipant.setMicrophoneEnabled(true);

  const agent = Array.from(nextRoom.remoteParticipants.values()).find(isAgent);
  if (agent) {
    cb.onAgentJoined();
    emitAgentState(agent, cb);
  }
};

/** Idempotent. Listeners are removed first so the store is not re-entered by `Disconnected`. */
export const disconnectVoiceRoom = async (): Promise<void> => {
  const current = room;
  if (!current) return;
  current.removeAllListeners();
  cleanupLocals();
  await current.disconnect().catch(() => undefined);
};

export const setVoiceMicEnabled = async (enabled: boolean): Promise<void> => {
  await room?.localParticipant.setMicrophoneEnabled(enabled);
};

/** Agent playback level 0..1; 0 when nothing is playing. */
export const getAgentAudioLevel = (): number => {
  if (!analyser) return 0;
  return Math.min(1, Math.max(0, analyser.calculateVolume()));
};

export const isVoiceRoomActive = (): boolean => room !== null;
